import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import useSubscriptionGate from '../../../hooks/useSubscriptionGate';
import { fetchUserFitnessProfiles } from '../../../utils/fitnessProfilesApi';
import { sortFitnessProfilesForDisplay } from '../../../utils/sortFitnessProfilesForDisplay';
import {
	aiTrainerListProfiles,
	resolveAiTrainerDeepLinkProfileId
} from '../../../utils/aiTrainerSession';
import {
	acceptTrainerChallenge,
	analyzeTrainerMeal,
	clearTrainerChat,
	dismissTrainerBriefing,
	fetchTrainer,
	sendTrainerChat,
	snoozeTrainerNudges,
	updateTrainerSettings
} from '../../../utils/aiTrainerApi';
import { compressImageForVision } from '../../../utils/compressImageForVision';
import useChallengeActions from '../../../hooks/useChallengeActions';
import useFitnessProfileChallenges from '../../../hooks/useFitnessProfileChallenges';
import AiTrainerCoachHeader from '../../AiTrainer/AiTrainerCoachHeader';
import AiTrainerSetupWizard from '../../AiTrainer/AiTrainerSetupWizard';
import AiTrainerCoachKnowledgeSheet from '../../AiTrainer/AiTrainerCoachKnowledgeSheet';
import AiTrainerSettingsSheet from '../../AiTrainer/AiTrainerSettingsSheet';
import AiTrainerAlertsRail from '../../AiTrainer/AiTrainerAlertsRail';
import AiTrainerChat from '../../AiTrainer/AiTrainerChat';
import AiTrainerProfileList from '../../AiTrainer/AiTrainerProfileList';
import BackToTopButton from '../../Buttons/BackToTopButton/BackToTopButton';
import {
	aiTrainerCardClassName,
	aiTrainerErrorClassName,
	aiTrainerHeadingClassName,
	aiTrainerLinkClassName,
	aiTrainerMutedTextClassName,
	aiTrainerPrimaryButtonClassName
} from '../../AiTrainer/aiTrainerStyles';

export default function AiTrainerPage({
	get_user_fitness_profile_trainer_endpoint: trainerEndpoint,
	update_user_fitness_profile_trainer_settings_endpoint: settingsEndpoint,
	chat_user_fitness_profile_trainer_endpoint: chatEndpoint,
	analyze_user_fitness_profile_trainer_meal_endpoint: analyzeMealEndpoint,
	clear_user_fitness_profile_trainer_chat_endpoint: clearChatEndpoint,
	accept_user_fitness_profile_trainer_challenge_endpoint: acceptChallengeEndpoint,
	dismiss_user_fitness_profile_trainer_briefing_endpoint: dismissBriefingEndpoint
}) {
	const { user, endpoints, csrfToken, activeFitnessProfile } = useAuth();
	const { guardSubscription } = useSubscriptionGate();
	const [searchParams] = useSearchParams();

	const [profiles, setProfiles] = useState([]);
	const [profileId, setProfileId] = useState(null);
	const [trainerData, setTrainerData] = useState(null);
	const [profilesLoading, setProfilesLoading] = useState(true);
	const [trainerLoading, setTrainerLoading] = useState(false);
	const [error, setError] = useState('');
	const [savingSetup, setSavingSetup] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(searchParams.get('settings') === '1');
	const [coachKnowledgeOpen, setCoachKnowledgeOpen] = useState(false);
	const [chatInput, setChatInput] = useState('');
	const [sending, setSending] = useState(false);
	const [analyzingMeal, setAnalyzingMeal] = useState(false);
	const [acceptingToken, setAcceptingToken] = useState(null);
	const [challengeActionLoading, setChallengeActionLoading] = useState(false);
	const [clearingChat, setClearingChat] = useState(false);
	const [snoozeLoading, setSnoozeLoading] = useState(false);
	const [quickPrompts, setQuickPrompts] = useState([]);
	const [challengeFocusNonce, setChallengeFocusNonce] = useState(0);
	const [showBackToTop, setShowBackToTop] = useState(false);
	const trainerRequestSeq = useRef(0);

	const beginTrainerRequest = useCallback(() => {
		trainerRequestSeq.current += 1;
		return trainerRequestSeq.current;
	}, []);

	const isLatestTrainerRequest = useCallback((requestId) => trainerRequestSeq.current === requestId, []);
	const selectedProfile = useMemo(
		() => profiles.find((profile) => profile.id === profileId),
		[profiles, profileId]
	);

	const listProfiles = useMemo(
		() => aiTrainerListProfiles({ profiles, activeFitnessProfile, user }),
		[profiles, activeFitnessProfile, user]
	);

	const showProfileList = !profileId;

	const loadProfiles = useCallback(async () => {
		if (!user?.id || !endpoints?.get_user_fitness_profiles_endpoint) return [];
		const data = await fetchUserFitnessProfiles({
			userId: user.id,
			endpoint: endpoints.get_user_fitness_profiles_endpoint,
			csrfToken
		});
		return sortFitnessProfilesForDisplay(data);
	}, [csrfToken, endpoints, user?.id]);

	const loadTrainer = useCallback(async ({ background = false } = {}) => {
		if (!user?.id || !profileId || !trainerEndpoint) return;

		const requestId = beginTrainerRequest();

		if (!background) {
			setTrainerLoading(true);
			setError('');
		}

		try {
			const response = await fetchTrainer({
				userId: user.id,
				fitnessProfileId: profileId,
				endpoint: trainerEndpoint,
				csrfToken
			});
			if (!isLatestTrainerRequest(requestId)) return;

			setTrainerData(response.data);
			setQuickPrompts(response.data.quick_prompts || []);
		} catch (err) {
			if (!isLatestTrainerRequest(requestId)) return;

			if (!background) {
				setError(err.message || 'Could not load trainer.');
				setTrainerData(null);
				setQuickPrompts([]);
			}
		} finally {
			if (!background && isLatestTrainerRequest(requestId)) setTrainerLoading(false);
		}
	}, [beginTrainerRequest, csrfToken, isLatestTrainerRequest, profileId, trainerEndpoint, user?.id]);

	const trainerActive = Boolean(
		profileId &&
			trainerData &&
			!trainerData?.settings?.needs_setup &&
			trainerData?.settings?.enabled !== false
	);

	const profileChallenges = useFitnessProfileChallenges({
		user,
		csrfToken,
		endpoints,
		fitnessProfileId: profileId,
		isPrimary: Boolean(selectedProfile?.is_primary),
		enabled: trainerActive
	});

	const refreshTrainerAndChallenges = useCallback(async () => {
		await Promise.all([loadTrainer({ background: true }), profileChallenges.loadChallenges()]);
	}, [loadTrainer, profileChallenges.loadChallenges]);

	const { runAction: runChallengeAction } = useChallengeActions({
		user,
		csrfToken,
		endpoints,
		onAfterAction: refreshTrainerAndChallenges
	});

	const handleChallengeAction = useCallback(
		async (challenge, action) => {
			const perform = async () => {
				setChallengeActionLoading(true);
				try {
					await runChallengeAction(challenge, action);
				} finally {
					setChallengeActionLoading(false);
				}
			};

			guardSubscription(perform);
		},
		[guardSubscription, runChallengeAction]
	);

	useEffect(() => {
		let cancelled = false;

		async function boot() {
			setProfilesLoading(true);
			setError('');

			try {
				const list = await loadProfiles();
				if (cancelled) return;
				setProfiles(list);

				const paramId = Number(searchParams.get('profile_id'));
				const deepLinkId = resolveAiTrainerDeepLinkProfileId({
					profileId: paramId,
					profiles: list,
					activeFitnessProfile
				});
				const visible = aiTrainerListProfiles({
					profiles: list,
					activeFitnessProfile,
					user
				});
				const initialId = deepLinkId || (visible.length === 1 ? visible[0].id : null);
				setProfileId(initialId);
			} catch (err) {
				if (!cancelled) setError(err.message || 'Could not load profiles.');
			} finally {
				if (!cancelled) setProfilesLoading(false);
			}
		}

		if (!user?.id) {
			setProfiles([]);
			setProfileId(null);
			setProfilesLoading(false);
			return undefined;
		}

		boot();
		return () => {
			cancelled = true;
		};
	}, [activeFitnessProfile, loadProfiles, searchParams, user]);

	useEffect(() => {
		if (profileId) {
			loadTrainer();
		} else {
			setTrainerData(null);
			setTrainerLoading(false);
		}
	}, [profileId, loadTrainer]);

	useEffect(() => {
		const handleScroll = () => {
			setShowBackToTop(window.scrollY > 420);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	function handleSelectProfile(id) {
		setError('');
		setProfileId(id);
	}

	function handleBackToProfiles() {
		setError('');
		setProfileId(null);
		setTrainerData(null);
		setSettingsOpen(false);
		setCoachKnowledgeOpen(false);
	}

	function handleOpenCoachKnowledge() {
		setCoachKnowledgeOpen(true);
		loadTrainer({ background: true });
	}

	async function performSetup(settings) {
		setSavingSetup(true);
		setError('');

		try {
			const response = await updateTrainerSettings({
				userId: user.id,
				fitnessProfileId: profileId,
				endpoint: settingsEndpoint,
				csrfToken,
				settings
			});
			setTrainerData(response.data);
		} catch (err) {
			setError(err.message || 'Could not save trainer settings.');
		} finally {
			setSavingSetup(false);
		}
	}

	function handleSetupComplete(settings) {
		guardSubscription(() => performSetup(settings));
	}

	async function handleSaveSettings(settings) {
		setSavingSetup(true);
		setError('');

		try {
			const response = await updateTrainerSettings({
				userId: user.id,
				fitnessProfileId: profileId,
				endpoint: settingsEndpoint,
				csrfToken,
				settings
			});
			setTrainerData(response.data);
			setSettingsOpen(false);
		} catch (err) {
			setError(err.message || 'Could not save settings.');
		} finally {
			setSavingSetup(false);
		}
	}

	async function handleSend(messageOverride) {
		const message = (messageOverride ?? chatInput).trim();
		if (!message) return;

		const send = async () => {
			setSending(true);
			setError('');
			setChatInput('');

			const pendingUserMessage = {
				id: `pending-user-${Date.now()}`,
				role: 'user',
				content: message,
				metadata: {},
				created_at: new Date().toISOString()
			};

			setTrainerData((previous) => {
				if (!previous) return previous;

				return {
					...previous,
					messages: [...(previous.messages || []), pendingUserMessage]
				};
			});

			try {
				const response = await sendTrainerChat({
					userId: user.id,
					fitnessProfileId: profileId,
					endpoint: chatEndpoint,
					csrfToken,
					message
				});
				const payload = response.data || {};
				setQuickPrompts(payload.quick_prompts || []);
				setTrainerData((previous) => {
					if (!previous || !payload.messages) return previous;

					return {
						...previous,
						messages: payload.messages
					};
				});
			} catch (err) {
				setError(err.message || 'Could not send message.');
				await loadTrainer({ background: true });
			} finally {
				setSending(false);
			}
		};

		guardSubscription(send);
	}

	function readFileAsDataUrl(file) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result);
			reader.onerror = () => reject(new Error('Could not read meal photo.'));
			reader.readAsDataURL(file);
		});
	}

	function handleMealPhotoSelected(file) {
		guardSubscription(async () => {
			if (!analyzeMealEndpoint || !profileId) return;

			setAnalyzingMeal(true);
			setError('');

			const pendingUserMessage = {
				id: `pending-meal-${Date.now()}`,
				role: 'user',
				content: "What's in this meal?",
				metadata: { meal_log: true },
				created_at: new Date().toISOString()
			};

			setTrainerData((previous) => {
				if (!previous) return previous;

				return {
					...previous,
					messages: [...(previous.messages || []), pendingUserMessage]
				};
			});

			try {
				const compressed = await compressImageForVision(file);
				const image = await readFileAsDataUrl(compressed);
				const response = await analyzeTrainerMeal({
					userId: user.id,
					fitnessProfileId: profileId,
					endpoint: analyzeMealEndpoint,
					csrfToken,
					image
				});
				const payload = response.data || {};
				setQuickPrompts(payload.quick_prompts || []);
				setTrainerData((previous) => {
					if (!previous || !payload.messages) return previous;

					return {
						...previous,
						messages: payload.messages
					};
				});
			} catch (err) {
				setError(err.message || 'Could not analyze meal photo.');
				await loadTrainer({ background: true });
			} finally {
				setAnalyzingMeal(false);
			}
		});
	}

	async function handleClearChat() {
		guardSubscription(async () => {
			if (!window.confirm('Clear this conversation and start a new chat? This cannot be undone.')) {
				return;
			}

			const requestId = beginTrainerRequest();
			setClearingChat(true);
			setError('');
			setChatInput('');
			setTrainerData((previous) => {
				if (!previous) return previous;

				return {
					...previous,
					messages: []
				};
			});

			try {
				const response = await clearTrainerChat({
					userId: user.id,
					fitnessProfileId: profileId,
					endpoint: clearChatEndpoint,
					csrfToken
				});
				if (!isLatestTrainerRequest(requestId)) return;

				setTrainerData(response.data);
				setQuickPrompts(response.data.quick_prompts || []);
			} catch (err) {
				if (!isLatestTrainerRequest(requestId)) return;

				setError(err.message || 'Could not clear chat.');
				await loadTrainer({ background: true });
			} finally {
				if (isLatestTrainerRequest(requestId)) setClearingChat(false);
			}
		});
	}

	async function handleAcceptChallenge(token) {
		const accept = async () => {
			setAcceptingToken(token);
			try {
				await acceptTrainerChallenge({
					userId: user.id,
					fitnessProfileId: profileId,
					endpoint: acceptChallengeEndpoint,
					csrfToken,
					confirmationToken: token
				});
				await refreshTrainerAndChallenges();
				setChallengeFocusNonce((value) => value + 1);
			} catch (err) {
				setError(err.message || 'Could not create challenge.');
			} finally {
				setAcceptingToken(null);
			}
		};

		guardSubscription(accept);
	}

	async function handleDismissBriefing() {
		const briefing = trainerData?.proactive_briefing;
		if (!briefing?.id) return;

		try {
			await dismissTrainerBriefing({
				userId: user.id,
				fitnessProfileId: profileId,
				briefingId: briefing.id,
				endpoint: dismissBriefingEndpoint,
				csrfToken
			});
			await loadTrainer({ background: true });
		} catch (err) {
			setError(err.message || 'Could not dismiss briefing.');
		}
	}

	async function applySnoozeResponse(response, { dismissBriefing = false } = {}) {
		const snoozeData = response?.data || {};
		setTrainerData((previous) => {
			if (!previous) return previous;

			return {
				...previous,
				settings: {
					...previous.settings,
					nudges_snoozed: snoozeData.nudges_snoozed ?? Boolean(snoozeData.snoozed_until),
					nudge_snoozed_until: snoozeData.snoozed_until || null
				}
			};
		});

		if (dismissBriefing) {
			await handleDismissBriefing();
		} else {
			await loadTrainer({ background: true });
		}
	}

	async function handleSnoozeNudges(hours, { dismissBriefing = false } = {}) {
		if (!endpoints?.snooze_user_fitness_profile_trainer_nudges_endpoint) return;

		setSnoozeLoading(true);
		setError('');

		try {
			const response = await snoozeTrainerNudges({
				userId: user.id,
				fitnessProfileId: profileId,
				endpoint: endpoints.snooze_user_fitness_profile_trainer_nudges_endpoint,
				csrfToken,
				hours: hours ?? 0
			});
			await applySnoozeResponse(response, { dismissBriefing });
		} catch (err) {
			setError(err.message || 'Could not snooze reminders.');
		} finally {
			setSnoozeLoading(false);
		}
	}

	async function handleResumeNudges() {
		if (!endpoints?.snooze_user_fitness_profile_trainer_nudges_endpoint) return;

		setSnoozeLoading(true);
		setError('');

		try {
			const response = await snoozeTrainerNudges({
				userId: user.id,
				fitnessProfileId: profileId,
				endpoint: endpoints.snooze_user_fitness_profile_trainer_nudges_endpoint,
				csrfToken,
				clear: true
			});
			await applySnoozeResponse(response);
		} catch (err) {
			setError(err.message || 'Could not resume reminders.');
		} finally {
			setSnoozeLoading(false);
		}
	}

	const needsSetup = trainerData?.settings?.needs_setup;
	const disabled = trainerData?.settings?.enabled === false && !needsSetup;
	const personality = trainerData?.settings?.personality || 'accountability_partner';
	const showTrainerHeader = profileId && trainerData && !needsSetup && !disabled;
	const nudgesSnoozed = Boolean(trainerData?.settings?.nudges_snoozed);
	const nudgeSnoozedUntil = trainerData?.settings?.nudge_snoozed_until || null;

	return (
		<div className="container mx-auto max-w-6xl px-4 pt-4 pb-10 lg:py-14 md:px-8">
			<div className="flex items-start justify-between gap-3">
				{showTrainerHeader ? (
					<AiTrainerCoachHeader
						personality={personality}
						onOpenCoachKnowledge={handleOpenCoachKnowledge}
						onOpenSettings={() => setSettingsOpen(true)}
					/>
				) : (
					<h1 className={`min-w-0 flex-1 text-2xl sm:text-3xl font-semibold tracking-tight ${aiTrainerHeadingClassName}`}>
						AI Trainer
					</h1>
				)}
				<Link
					to="/fitness-hub"
					className="shrink-0 self-start text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
				>
					← Fitness Hub
				</Link>
			</div>

			{!showProfileList && listProfiles.length > 1 && (
				<button type="button" onClick={handleBackToProfiles} className={`mt-4 block ${aiTrainerLinkClassName}`}>
					← All profiles
				</button>
			)}

			<div className="mt-6 space-y-6">
				{error && <div className={`${aiTrainerErrorClassName} p-3`}>{error}</div>}

				{profilesLoading && <p className={aiTrainerMutedTextClassName}>Loading profiles…</p>}

				{!profilesLoading && !user?.id && (
					<div className={`${aiTrainerCardClassName} p-8`}>
						<p className={aiTrainerMutedTextClassName}>Sign in to set up your AI trainer.</p>
					</div>
				)}

				{!profilesLoading && user?.id && profiles.length === 0 && (
					<div className={`${aiTrainerCardClassName} p-8 text-center`}>
						<p className={aiTrainerMutedTextClassName}>
							Create a fitness profile to enable your AI trainer.
						</p>
						<Link to="/fitness-profiles/new" className={`mt-4 inline-block ${aiTrainerLinkClassName}`}>
							Create profile
						</Link>
					</div>
				)}

				{!profilesLoading && showProfileList && listProfiles.length > 0 && (
					<AiTrainerProfileList profiles={listProfiles} onSelectProfile={handleSelectProfile} />
				)}

				{!profilesLoading && showProfileList && user?.id && profiles.length > 0 && listProfiles.length === 0 && (
					<div className={`${aiTrainerCardClassName} p-8`}>
						<p className={aiTrainerMutedTextClassName}>
							No fitness profile is linked to your login. Sign in with your profile email or contact
							the account owner.
						</p>
					</div>
				)}

				{profileId && trainerLoading && !trainerData && (
					<p className={aiTrainerMutedTextClassName}>Loading trainer…</p>
				)}

				{profileId && trainerData && needsSetup && selectedProfile && (
					<AiTrainerSetupWizard
						profileName={selectedProfile.name}
						contextSnapshot={trainerData?.context_snapshot}
						onComplete={handleSetupComplete}
						saving={savingSetup}
						error={error}
					/>
				)}

				{profileId && trainerData && disabled && (
					<div className={`${aiTrainerCardClassName} p-8 text-center`}>
						<p className={aiTrainerMutedTextClassName}>AI trainer is paused for this profile.</p>
						<button
							type="button"
							onClick={() => setSettingsOpen(true)}
							className={`mt-4 ${aiTrainerPrimaryButtonClassName}`}
						>
							Turn trainer back on
						</button>
					</div>
				)}

				{showTrainerHeader && (
					<AiTrainerChat
						messages={trainerData.messages || []}
						personality={personality}
						fitnessProfileId={profileId}
						input={chatInput}
						onInputChange={setChatInput}
						onSend={() => handleSend()}
						sending={sending}
						analyzingMeal={analyzingMeal}
						onMealPhotoSelected={analyzeMealEndpoint ? handleMealPhotoSelected : undefined}
						onAcceptChallenge={handleAcceptChallenge}
						acceptingToken={acceptingToken}
						quickPrompts={quickPrompts}
						onQuickPrompt={(prompt) => handleSend(prompt)}
						onClearChat={handleClearChat}
						clearingChat={clearingChat}
					/>
				)}
			</div>

			<AiTrainerCoachKnowledgeSheet
				open={coachKnowledgeOpen}
				coachKnowledge={trainerData?.coach_knowledge}
				fitnessProfileId={profileId}
				personality={personality}
				loading={trainerLoading}
				onClose={() => setCoachKnowledgeOpen(false)}
			/>

			<AiTrainerSettingsSheet
				open={settingsOpen}
				settings={trainerData?.settings}
				onClose={() => setSettingsOpen(false)}
				onSave={handleSaveSettings}
				onSnoozeNudges={handleSnoozeNudges}
				onResumeNudges={handleResumeNudges}
				snoozeLoading={snoozeLoading}
				saving={savingSetup}
				error={error}
			/>

			{showTrainerHeader && (
				<AiTrainerAlertsRail
					briefing={trainerData.proactive_briefing}
					onDismissBriefing={handleDismissBriefing}
					onSnooze={(hours) => handleSnoozeNudges(hours, { dismissBriefing: true })}
					onRunAction={handleChallengeAction}
					actionLoading={challengeActionLoading}
					nudgesSnoozed={nudgesSnoozed}
					nudgeSnoozedUntil={nudgeSnoozedUntil}
					challenges={profileChallenges.challenges}
					challengesLoading={profileChallenges.loading}
					challengesError={profileChallenges.error}
					onDayCheck={profileChallenges.handleDayCheck}
					isDayCheckPending={profileChallenges.isDayCheckPending}
					challengeFocusNonce={challengeFocusNonce}
				/>
			)}

			{showBackToTop && <BackToTopButton />}
		</div>
	);
}
